import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Users, Tag, ShieldCheck, HeartHandshake, Lock, Sparkles,
} from 'lucide-react';
import { BRAND_NAME } from '../utils/brand';
import { trackCtaClick, trackWaitlistSubmitted } from '../utils/analytics';
import Reveal from '../components/Reveal';
import LeafDecor from '../components/LeafDecor';
import EarlyAccessForm from '../components/EarlyAccessForm';

//
// /preventive-health — Phase 2 waitlist / "Join the Movement" page.
//
// Client redesign (mockup 2026-07-28): dark-teal hero with a waitlist card
// overlay, a "Together, We Are Stronger" mission block, a three-icon "Why
// Your Support Matters" grid, a second waitlist block, and legal footnotes.
// Nothing on this page implies an active service — Phase 2 is coming soon
// per Website Build Handoff V1.0 §§ 1, 3, 9.
//

const WAITLIST_KEY = 'mdai_preventive_waitlist_v1';

const WAITLIST_BENEFITS = [
  'Receive priority launch updates',
  'Get early access to programme details',
  'Help us work toward better-value packages for everyone',
];

const WHY_MATTERS = [
  {
    icon: Users,
    title: 'Community-Powered',
    body: 'The more people who join, the stronger our collective voice.',
  },
  {
    icon: Tag,
    title: 'Better Value',
    body: 'We aim to work toward packages that provide greater value at a more accessible cost.',
  },
  {
    icon: ShieldCheck,
    title: 'Professional Guidance',
    body: 'Future programme services will involve participating licensed healthcare providers.',
  },
];

export default function PreventiveHealth() {
  // SEO title / meta / canonical are set by the global <SEO /> in App.js
  // using src/utils/seoConfig.js (§/preventive-health and
  // §/preventive-health/waitlist entries).
  return (
    <>
      <Hero />
      <TogetherSection />
      <WhyMattersSection />
      <SecondaryWaitlist />
      <LegalNotes />
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sections
// ────────────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative isolate overflow-hidden text-white">
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background: `
            radial-gradient(60% 55% at 85% 25%, rgba(14,178,171,0.35), transparent 65%),
            radial-gradient(50% 45% at 8% 85%, rgba(2,62,115,0.55), transparent 70%),
            linear-gradient(180deg, #02213F 0%, #023E73 60%, #024E80 100%)
          `,
        }}
      />

      <div className="container-x pt-10 pb-16 sm:pt-16 sm:pb-24 max-w-3xl">
        <Reveal>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-600/25 border border-brand-400/30 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-100">
            <Sparkles size={12} className="text-brand-200" /> Phase 2 · Coming Soon
          </span>

          <h1 className="mt-5 font-bold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.1]">
            Join the Movement for
            <br />
            Better-Value Preventive Health
          </h1>
          <p className="mt-4 text-sm sm:text-base text-white/85 max-w-xl leading-relaxed">
            Support {BRAND_NAME}'s mission to make preventive health more accessible
            and worthwhile for Malaysians.
          </p>

          <p className="mt-6 text-sm font-semibold text-white/90">Join the waitlist to:</p>
          <ul className="mt-3 space-y-2.5">
            {WAITLIST_BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm text-white/90">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-500 text-white shrink-0 mt-0.5">
                  <CheckCircle2 size={14} strokeWidth={2.25} />
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={150}>
          <div id="early-access" className="scroll-mt-20">
            <WaitlistCard variant="hero" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function TogetherSection() {
  return (
    <section className="bg-white">
      <div className="container-x py-14 sm:py-20 max-w-4xl">
        <Reveal>
          <div className="grid sm:grid-cols-[auto_1fr] gap-5 sm:gap-8 items-start">
            <span className="shrink-0 inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-brand-50 text-brand-600 mx-auto sm:mx-0">
              <HeartHandshake size={40} strokeWidth={1.75} />
            </span>
            <div className="text-center sm:text-left">
              <h2 className="text-xl sm:text-2xl font-bold text-brand-700">
                Together, We Are Stronger
              </h2>
              <p className="mt-3 text-sm sm:text-base text-ink-700 leading-relaxed">
                Your email helps us demonstrate real community demand and strengthens
                our ability to work toward higher-value preventive health packages
                for everyone.
              </p>
              <p className="mt-3 text-sm sm:text-base text-ink-700 leading-relaxed">
                Support the mission — and become one of the first to benefit when
                the programme is ready.
              </p>
              <a
                href="#early-access"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById('early-access');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  trackCtaClick({
                    page: 'preventive_health',
                    button_label: 'Support the Mission',
                    destination: '#early-access',
                  });
                }}
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-brand-500 text-brand-700 font-semibold px-5 py-2.5 text-sm hover:bg-brand-50 transition-colors"
              >
                Support the Mission <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function WhyMattersSection() {
  return (
    <section className="bg-white pb-12 sm:pb-16">
      <div className="container-x max-w-5xl">
        <Reveal>
          <h2 className="text-center text-xl sm:text-2xl font-bold text-brand-700">
            Why Your Support Matters
          </h2>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          {WHY_MATTERS.map(({ icon: Icon, title, body }, i) => (
            <Reveal key={title} delay={i * 100}>
              <div className="h-full rounded-2xl border border-ink-100 bg-white shadow-soft p-6 text-center">
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-50 text-brand-600 mb-3">
                  <Icon size={24} strokeWidth={1.75} />
                </span>
                <h3 className="text-sm sm:text-base font-bold text-brand-700">{title}</h3>
                <p className="mt-2 text-xs sm:text-sm text-ink-600 leading-relaxed">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function SecondaryWaitlist() {
  return (
    <section id="waitlist" className="relative overflow-hidden scroll-mt-16 bg-brand-50/60 border-y border-brand-100">
      <LeafDecor />
      <div className="relative container-x py-12 sm:py-16 max-w-3xl mx-auto text-center">
        <Reveal>
          <h2 className="text-xl sm:text-2xl font-bold text-brand-700 leading-tight">
            One Email Can Help Build
            <br />
            Something Better for Everyone
          </h2>
          <p className="mt-3 text-sm text-ink-700">
            Join the {BRAND_NAME} community and be among the first to receive programme updates.
          </p>
          <div className="mt-6">
            <a
              href="#early-access"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById('early-access');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                trackCtaClick({
                  page: 'preventive_health',
                  button_label: 'Join Early Access',
                  destination: '#early-access',
                });
              }}
              className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors"
            >
              Join Early Access <ArrowRight size={16} />
            </a>
          </div>
          <p className="mt-4 text-xs sm:text-sm text-ink-600">
            Together, we are stronger — and together, we can work toward better value for all.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function LegalNotes() {
  return (
    <section className="bg-white">
      <div className="container-x py-8 max-w-4xl space-y-3">
        <div className="flex items-start gap-3 rounded-xl p-4 border border-ink-100 bg-white">
          <ShieldCheck size={18} className="text-brand-600 shrink-0 mt-0.5" />
          <p className="text-xs text-ink-600 leading-relaxed">
            Joining the waitlist does not create a doctor–patient relationship and is not a
            request for medical care. Programme details, pricing, availability and participating
            healthcare providers have not yet been finalised.
          </p>
        </div>
        <div className="flex items-start gap-3 rounded-xl p-4 border border-ink-100 bg-white">
          <Lock size={18} className="text-brand-600 shrink-0 mt-0.5" />
          <p className="text-xs text-ink-600 leading-relaxed">
            By submitting this form, you agree to receive {BRAND_NAME} programme updates and
            acknowledge our{' '}
            <Link to="/privacy" className="text-brand-700 underline hover:no-underline">
              Privacy Policy
            </Link>
            . You may unsubscribe at any time.
          </p>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Waitlist card — shared by the hero (white overlay) and the secondary CTA.
// Two-step Early Access form; see components/EarlyAccessForm.js.
// ────────────────────────────────────────────────────────────────────────────

function WaitlistCard({ variant = 'hero' }) {
  return (
    <EarlyAccessForm
      locale="en"
      source="preventive_health"
      variant={variant}
      storageKey={WAITLIST_KEY}
      onSubmitted={() => {
        trackCtaClick({
          page: 'preventive_health',
          button_label: 'Join Early Access',
          destination: `preventive_health_waitlist_${variant}`,
        });
        // GA4 conversion event per SEO Master §6.
        trackWaitlistSubmitted({ source_page: 'preventive_health', list: 'preventive_health' });
      }}
    />
  );
}
